import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { sendToApi } from 'api/client'
import { UseMutationOptions } from 'react-query/types/react/types'
import { Data } from 'hooks/queries/workspaces/useCreateBrandVisualKit'
import { reportError } from 'utils/error'

interface Props {
  id: number
  brandVisualKitId: number
}

export type Options = Omit<UseMutationOptions<Data, Error, Props>, 'mutationKey' | 'mutationFn'>

const deleteBrandMediaFile = (id: number, brandVisualKitId: number, workspaceId: number) =>
  sendToApi(`/w/${workspaceId}/brand_visual_kits/${brandVisualKitId}/brand_media_files/${id}`, { method: 'DELETE' })

export const useDeleteBrandMediaFile = (options?: Options) => {
  const queryClient = useQueryClient()
  const currentWorkspaceId = useCurrentWorkspaceId()
  return useMutation(
    'deleteBrandMediaFile',
    async ({ id, brandVisualKitId }: Props) => {
      const response = await deleteBrandMediaFile(id, brandVisualKitId, currentWorkspaceId)
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        reportError('Failed to delete brand media file', {
          metaData: {
            id: id,
            response: response.data,
          },
        })
        throw new Error()
      }
      return response.data
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits'])
        queryClient.invalidateQueries([
          'workspaces',
          currentWorkspaceId,
          'brand_visual_kits',
          variables.brandVisualKitId,
        ])
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
